import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72034c2-4f95-4f18-be74-1d29b1871fdc/artifacts/djdyqu9t_IMG_1381.png";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Mínimo 6 caracteres"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al restablecer");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F5EEDB] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-[#5B3E2E]/60">Token no válido</p>
          <Link to="/login" className="text-sm text-[#E48D57] mt-2 inline-block">Ir al login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5EEDB] flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Refugium" className="w-16 h-16 rounded-full mx-auto mb-4" />
          <h1 className="font-heading text-3xl text-[#5B3E2E]">Nueva contraseña</h1>
        </div>

        {success ? (
          <div className="bg-[#FDFAF4] rounded-3xl p-8 border border-[#5B3E2E]/10 text-center">
            <div className="w-16 h-16 rounded-full bg-[#6B9070]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={28} className="text-[#6B9070]" />
            </div>
            <h2 className="font-heading text-xl text-[#5B3E2E] mb-2">Contrasena restablecida</h2>
            <p className="text-sm text-[#5B3E2E]/60 mb-4">Ya puedes iniciar sesión con tu nueva contraseña.</p>
            <Button data-testid="go-login-btn" onClick={() => navigate("/login")} className="bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full px-6">
              Ir al login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#FDFAF4] rounded-3xl p-8 border border-[#5B3E2E]/10">
            {error && <div className="bg-[#D9534F]/10 text-[#D9534F] text-sm rounded-xl p-3 mb-4">{error}</div>}
            <div className="mb-4">
              <Label className="text-[#5B3E2E] text-sm">Nueva contraseña</Label>
              <div className="relative mt-1">
                <Input
                  data-testid="reset-password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E] pr-10"
                  placeholder="Mínimo 6 caracteres"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B3E2E]/40">
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button data-testid="reset-submit" type="submit" disabled={submitting} className="w-full bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full py-3 font-medium">
              {submitting ? "Restableciendo..." : "Restablecer contraseña"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
